<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import MessageTemplatesAPI from 'dashboard/api/messageTemplates';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WhatsappPhonePreview from 'dashboard/components-next/template-preview/WhatsappPhonePreview.vue';
import MessageTemplateForm from './MessageTemplateForm.vue';

const props = defineProps({
  inboxId: { type: [String, Number], required: true },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();

const dialogRef = ref(null);
const formRef = ref(null);
const isSaving = ref(false);

const open = () => dialogRef.value?.open();
const close = () => dialogRef.value?.close();

// When the channel lost access to its WhatsApp account/phone number at Meta (revoked
// permission, disconnected WABA), the API returns a generic Graph API message that isn't
// actionable for the user — show a clear "reconnect the channel" message instead.
const templateErrorMessage = error => {
  const { code, error: serverError } = error?.response?.data || {};
  if (code === 'reauthorization_required') {
    return t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.REAUTHORIZATION_ERROR');
  }
  return serverError || t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.SAVE_ERROR');
};

const saveDraft = async () => {
  if (!formRef.value?.validate()) return;

  isSaving.value = true;
  try {
    const { data } = await MessageTemplatesAPI.create(
      props.inboxId,
      formRef.value.getPayload()
    );
    useAlert(t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.DRAFT_SAVED'));
    emit('saved', data.payload);
    formRef.value.resetForm();
    close();
  } catch (error) {
    useAlert(templateErrorMessage(error));
  } finally {
    isSaving.value = false;
  }
};

const submitForApproval = async () => {
  if (!formRef.value?.validate()) return;

  isSaving.value = true;
  try {
    const { data } = await MessageTemplatesAPI.create(
      props.inboxId,
      formRef.value.getPayload()
    );
    await MessageTemplatesAPI.submit(props.inboxId, data.payload.id);
    useAlert(t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.SUBMITTED'));
    emit('saved', data.payload);
    formRef.value.resetForm();
    close();
  } catch (error) {
    useAlert(templateErrorMessage(error));
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    :title="t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.TITLE')"
    @close="formRef?.resetForm()"
  >
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
      <MessageTemplateForm ref="formRef" :inbox-id="inboxId" />

      <div
        class="flex flex-col gap-2 rounded-lg bg-n-slate-2 p-4 md:sticky md:top-0"
      >
        <span class="text-xs font-medium text-n-slate-11">{{
          t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.PREVIEW')
        }}</span>
        <WhatsappPhonePreview
          v-bind="formRef?.previewData"
          :empty-text="t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.PREVIEW_EMPTY')"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          @click="close"
        />
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.SAVE_DRAFT')"
          :is-loading="isSaving"
          @click="saveDraft"
        />
        <Button
          type="button"
          :label="t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.SUBMIT')"
          :is-loading="isSaving"
          @click="submitForApproval"
        />
      </div>
    </template>
  </Dialog>
</template>
